import React, { useState, useEffect } from "react";
import { useTitle } from "../../hooks/useTitle";
import { Link } from "react-router-dom";
import { TrendingUp, ShoppingBag, Package, Users } from "lucide-react";
import {
  tablesDB,
  DB_ID,
  ORDERS_TABLE,
  PRODUCTS_TABLE,
  USERS_TABLE,
} from "../../appwrite";
import { Query } from "appwrite";
import "./Dashboard.css";

const fmtNaira = (v) =>
  "₦" +
  parseFloat(v || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

const fmtDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const parseInfoArray = (arr) => {
  if (!Array.isArray(arr)) return {};
  const r = {};
  arr.forEach((item) => {
    if (typeof item === "string") {
      try { Object.assign(r, JSON.parse(item)); }
      catch { const i = item.indexOf(":"); if (i > -1) r[item.slice(0, i).trim()] = item.slice(i + 1).trim(); }
    }
  });
  return r;
};

const PER = 6;

function Pagination({ page, setPage, total }) {
  const pages = Math.ceil(total / PER);
  if (pages <= 1) return null;
  return (
    <div className="db-pagination">
      <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
        ← Prev
      </button>
      <span>
        {page} / {pages}
      </span>
      <button disabled={page === pages} onClick={() => setPage((p) => p + 1)}>
        Next →
      </button>
    </div>
  );
}

export default function Dashboard() {
  useTitle("Dashboard - TrendBay Admin");

  const [stats, setStats] = useState({
    sales: 0,
    orders: 0,
    sold: 0,
    customers: 0,
  });
  const [pendingOrders, setPending] = useState([]);
  const [bestsellers, setBest] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [oPage, setOPage] = useState(1);
  const [pPage, setPPage] = useState(1);
  const [cPage, setCPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const [ordersRes, usersRes] = await Promise.all([
          tablesDB.listRows({
            databaseId: DB_ID,
            tableId: ORDERS_TABLE,
            queries: [Query.limit(1000)],
          }),
          tablesDB.listRows({
            databaseId: DB_ID,
            tableId: USERS_TABLE,
            queries: [Query.limit(1000)],
          }),
        ]);

        const orders = ordersRes.rows || ordersRes.documents || [];
        const users = usersRes.rows || usersRes.documents || [];

        let totalSales = 0,
          soldCount = 0;
        const pending = [];
        const productMap = {};

        orders.forEach((o) => {
          totalSales += parseFloat(o.orderTotal) || 0;

          // Cart items are stored as JSON strings in order.cart, not in CART_TABLE
          const parsedCart = (o.cart || []).map((item) => {
            if (typeof item === "string") { try { return JSON.parse(item); } catch { return {}; } }
            return item;
          });
          const qty = parsedCart.reduce((s, c) => s + (c.quantity || 1), 0);
          soldCount += qty;

          if (o.fulfillmentStatus === "pending") {
            const info = parseInfoArray(o.shippingInfo);
            pending.push({
              id: o.$id,
              orderNumber: o.orderNumber,
              date: o.$createdAt,
              customer: info.fullName || "Unknown",
              items: qty,
            });
          }

          // Also feed parsedCart into productMap for Top Products
          parsedCart.forEach((item) => {
            const pid = item.productId || item.id || item.products;
            if (!pid) return;
            if (!productMap[pid])
              productMap[pid] = { id: pid, name: item.productName || item.title || "Unknown", qty: 0, total: 0 };
            productMap[pid].qty += item.quantity || 1;
            productMap[pid].total += (parseFloat(item.price) || 0) * (item.quantity || 1);
          });
        });

        pending.sort((a, b) => new Date(b.date) - new Date(a.date));

        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        const newCust = users.filter(
          (u) => (u.userRole || u.role) !== "admin" && new Date(u.$createdAt) >= oneMonthAgo,
        ).length;
        const sortedUsers = [...users]
          .filter((u) => (u.userRole || u.role) !== "admin")
          .sort((a, b) => new Date(b.$createdAt) - new Date(a.$createdAt));

        setStats({
          sales: totalSales,
          orders: orders.length,
          sold: soldCount,
          customers: newCust,
        });
        setPending(pending);
        setBest(
          Object.values(productMap)
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 20),
        );
        setCustomers(sortedUsers);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const STAT_CARDS = [
    {
      label: "Total Sales",
      value: fmtNaira(stats.sales),
      icon: TrendingUp,
      color: "#00A855",
      bg: "#f0fdf6",
    },
    {
      label: "Total Orders",
      value: stats.orders,
      icon: ShoppingBag,
      color: "#3b82f6",
      bg: "#eff6ff",
    },
    {
      label: "Products Sold",
      value: stats.sold,
      icon: Package,
      color: "#f59e0b",
      bg: "#fffbeb",
    },
    {
      label: "New Customers",
      value: stats.customers,
      icon: Users,
      color: "#8b5cf6",
      bg: "#f5f3ff",
    },
  ];

  const oSlice = pendingOrders.slice((oPage - 1) * PER, oPage * PER);
  const pSlice = bestsellers.slice((pPage - 1) * PER, pPage * PER);
  const cSlice = customers.slice((cPage - 1) * PER, cPage * PER);

  return (
    <div className="db-page">
      {/* Stat cards */}
      <div className="db-stats">
        {STAT_CARDS.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="db-stat-card">
            <div className="db-stat-icon" style={{ background: bg }}>
              <Icon size={20} color={color} />
            </div>
            <div>
              <p className="db-stat-value">{value}</p>
              <p className="db-stat-label">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Pending Orders */}
      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">Orders Awaiting Fulfillment</h3>
          <Link to="/d/orders" className="db-card-link">View all →</Link>
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Order</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {oSlice.length === 0 ? (
                <tr><td colSpan={6} className="db-empty">No pending orders 🎉</td></tr>
              ) : (
                oSlice.map((o, i) => (
                  <tr key={o.id}>
                    <td className="db-td-num">{(oPage - 1) * PER + i + 1}</td>
                    <td><Link to={`/d/order-details/${o.id}`} className="db-order-link">#{o.orderNumber || "-"}</Link></td>
                    <td>{fmtDate(o.date)}</td>
                    <td>{o.customer}</td>
                    <td>{o.items}</td>
                    <td><span className="db-badge db-badge-pending">Pending</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="db-mobile-cards">
          {oSlice.length === 0 ? (
            <p className="db-empty">No pending orders 🎉</p>
          ) : (
            oSlice.map((o) => (
              <Link to={`/d/order-details/${o.id}`} className="db-mobile-card" key={o.id}>
                <div className="db-mc-top">
                  <span className="db-order-link">#{o.orderNumber || "-"}</span>
                  <span className="db-badge db-badge-pending">Pending</span>
                </div>
                <div className="db-mc-mid">
                  <span>{o.customer}</span>
                  <span className="db-mc-items">{o.items} item{o.items !== 1 ? "s" : ""}</span>
                </div>
                <span className="db-mc-date">{fmtDate(o.date)}</span>
              </Link>
            ))
          )}
        </div>
        <Pagination page={oPage} setPage={setOPage} total={pendingOrders.length} />
      </div>

      {/* Top Products */}
      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">Top Products</h3>
          <Link to="/d/all-products" className="db-card-link">View all →</Link>
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Units Sold</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {pSlice.length === 0 ? (
                <tr><td colSpan={4} className="db-empty">No sales data yet</td></tr>
              ) : (
                pSlice.map((p, i) => (
                  <tr key={p.id}>
                    <td className="db-td-num">{(pPage - 1) * PER + i + 1}</td>
                    <td>{p.name}</td>
                    <td><strong>{p.qty}</strong></td>
                    <td className="db-revenue">{fmtNaira(p.total)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="db-mobile-cards">
          {pSlice.length === 0 ? (
            <p className="db-empty">No sales data yet</p>
          ) : (
            pSlice.map((p) => (
              <div className="db-mobile-card" key={p.id}>
                <div className="db-mc-top">
                  <span className="db-mc-name">{p.name}</span>
                </div>
                <div className="db-mc-mid">
                  <span>{p.qty} sold</span>
                  <span className="db-revenue">{fmtNaira(p.total)}</span>
                </div>
              </div>
            ))
          )}
        </div>
        <Pagination page={pPage} setPage={setPPage} total={bestsellers.length} />
      </div>

      {/* Customers */}
      <div className="db-card">
        <div className="db-card-header">
          <h3 className="db-card-title">Customers</h3>
        </div>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {cSlice.length === 0 ? (
                <tr><td colSpan={5} className="db-empty">No customers yet</td></tr>
              ) : (
                cSlice.map((c, i) => (
                  <tr key={c.$id || c.email}>
                    <td className="db-td-num">{(cPage - 1) * PER + i + 1}</td>
                    <td>{c.fullName || c.name || "-"}</td>
                    <td className="db-email">{c.email}</td>
                    <td><span className={`db-badge db-role-${c.userRole || c.role || "user"}`}>{c.userRole || c.role || "user"}</span></td>
                    <td>{fmtDate(c.$createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="db-mobile-cards">
          {cSlice.length === 0 ? (
            <p className="db-empty">No customers yet</p>
          ) : (
            cSlice.map((c) => (
              <div className="db-mobile-card" key={c.$id || c.email}>
                <div className="db-mc-top">
                  <span className="db-mc-name">{c.fullName || c.name || "-"}</span>
                  <span className={`db-badge db-role-${c.userRole || c.role || "user"}`}>{c.userRole || c.role || "user"}</span>
                </div>
                <div className="db-mc-mid">
                  <span className="db-email">{c.email}</span>
                </div>
                <span className="db-mc-date">{fmtDate(c.$createdAt)}</span>
              </div>
            ))
          )}
        </div>
        <Pagination page={cPage} setPage={setCPage} total={customers.length} />
      </div>
    </div>
  );
}
